import { zodResolver } from '@hookform/resolvers/zod';
import Delete from '@mui/icons-material/Delete';
import Save from '@mui/icons-material/Save';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { FieldErrors, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import AuthGuard from '@/components/auth-guard';
import { BasicIdentitySection } from '@/components/event-form/basic-identity';
import { CustomFieldsSection } from '@/components/event-form/custom-fields-section';
import { RelationshipsSection } from '@/components/event-form/relationships';
import type { EventEntityChangeWithFailure } from '@/components/event-implications/event-change-card';
import { EventImplicationsSection } from '@/components/event-implications/event-implications-section';
import { FloatingSaveButton } from '@/components/floating-save-button';
import { DeleteConfirmationModal } from '@/components/mui-delete-confirmation-modal';
import { Page } from '@/components/page';
import {
  EventFormSchema,
  EventFormValues,
  useEventAccessLayer
} from '@/features/events';
import { useFirebase } from '@/firebase';
import { useToast } from '@/hooks/use-toast';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import type { FieldChange, PrimaryEntityType } from '@/lib/types';
import { generateReciprocalDeltas } from '@/lib/utils/generate-reciprocal-deltas';
import { splitBySchemaShape } from '@/lib/utils/split-by-schema';

type PendingChange = {
  entityId: string;
  entityType: PrimaryEntityType;
  entityName: string;
  changes: FieldChange[];
  description: string;
  isDeleted: boolean;
};

export const Route = createFileRoute('/events/edit/$id')({
  component: EditEventPage,
});

function EditEventForm() {
  const navigate = useNavigate();
  const { id: eventId } = Route.useParams();
  const { backend } = useFirebase();

  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [lastResetId, setLastResetId] = useState<string | null>(null);
  const [failedImplications, setFailedImplications] = useState<Array<{ entityId: string, entityType: PrimaryEntityType, entityName: string, changes: FieldChange[], description: string, failedToSave?: boolean }>>([]);

  // Single map for all pending changes
  const [pendingChanges, setPendingChanges] = useState<Map<string, PendingChange>>(new Map());

  const { novel } = useNovelRepository();
  const novelId = novel?.id;
  const { toast } = useToast();
  const { t } = useTranslation();

  useEffect(() => {
    if (eventId) {
      const key = `failedImplications_${eventId}`;
      const stored = sessionStorage.getItem(key);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          setFailedImplications(parsed);
          sessionStorage.removeItem(key);
        } catch (e) {
          console.error('Failed to parse failed implications:', e);
        }
      }
    }
  }, [eventId]);

  const {
    event: eventData,
    sections: eventSettings,
    isLoading: isLoadingLayer,
    updateEvent: updateEventLayer,
    deleteEvent: deleteEventLayer
  } = useEventAccessLayer(eventId);

  const methods = useForm<EventFormValues>({
    resolver: zodResolver(EventFormSchema),
    defaultValues: {
      name: '',
      type: '',
      date: '',
      shortDescription: '',
      whyItMatters: '',
      characterIds: [],
      organizationIds: [],
      locationIds: [],
    },
  });
  const { reset } = methods;

  useEffect(() => {
    if (eventData && !isLoadingLayer && lastResetId !== eventId) {
      reset(eventData);
      setLastResetId(eventId);
      setIsLoading(false);
    } else if (!isLoadingLayer) {
      setIsLoading(false);
    }
  }, [eventData, reset, isLoadingLayer, eventId, lastResetId]);

  const effectiveEventChanges = useMemo(() => {
    const rawEventChanges: EventEntityChangeWithFailure[] = (eventData?.eventChanges as EventEntityChangeWithFailure[]) || [];

    const baseChanges: EventEntityChangeWithFailure[] = [
      ...rawEventChanges,
      ...failedImplications.map(imp => ({
        entityType: imp.entityType,
        entityId: imp.entityId,
        entityName: imp.entityName,
        changes: imp.changes,
        description: imp.description,
        failedToSave: true,
      }))
    ];

    const map = new Map<string, EventEntityChangeWithFailure>(
      baseChanges.map(c => [c.entityId, { ...c }])
    );

    // Overlay pendingChanges
    for (const pending of pendingChanges.values()) {
      if (pending.isDeleted) {
        map.delete(pending.entityId);
      } else {
        map.set(pending.entityId, {
          entityType: pending.entityType,
          entityId: pending.entityId,
          entityName: pending.entityName,
          changes: pending.changes,
          description: pending.description,
        });
      }
    }

    return Array.from(map.values());
  }, [eventData, failedImplications, pendingChanges]);

  const onSubmit = async (values: EventFormValues) => {
    if (!novelId) return;

    setIsSaving(true);
    try {
      const { defined: coreData, details } = splitBySchemaShape(EventFormSchema, values);

      delete details.eventChanges;
      delete (coreData as Record<string, unknown>).eventChanges;

      await updateEventLayer(eventId, {
        ...coreData,
        id: eventId,
        details,
        audit: {
          ...eventData?.audit,
          updatedAt: new Date().toISOString(),
        },
      });

      if (pendingChanges.size > 0 && novel && backend) {
        for (const pending of pendingChanges.values()) {
          if (pending.isDeleted) {
            await backend.entities.deleteEventImplication(
              novel.ownerId,
              novel,
              eventId,
              pending.entityType,
              pending.entityId
            );
          } else {
            await backend.entities.saveEventImplication(
              novel.ownerId,
              novel,
              eventId,
              values.name || eventData?.name || '',
              values.order ?? eventData?.order ?? 0,
              pending.entityType,
              pending.entityId,
              pending.entityName,
              pending.changes,
              pending.description,
              true
            );
          }
        }
      }

      toast({
        title: t('entity_form.events.update_success_title'),
        description: t('entity_form.events.update_success_desc', { name: values.name }),
      });

      navigate({ to: '/events' });
    } catch (error) {
      console.error('Submit error:', error);
      toast({
        title: t('entity_form.common.update_failed_title'),
        description: error instanceof Error ? error.message : t('entity_form.common.unexpected_error'),
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!eventId) return;

    setIsDeleting(true);
    try {
      await deleteEventLayer(eventId);

      toast({
        title: t('entity_form.events.delete_success_title'),
        description: t('entity_form.events.delete_success_desc'),
      });

      navigate({ to: '/events' });
    } catch (error) {
      console.error('Delete error:', error);
      toast({
        title: t('entity_form.common.delete_failed_title'),
        description: error instanceof Error ? error.message : t('entity_form.common.unexpected_error'),
        variant: 'destructive',
      });
      setIsDeleting(false);
    }
  };

  const onError = (errors: FieldErrors<EventFormValues>) => {
    console.error('Form validation errors:', errors);
    const firstErrorKey = Object.keys(errors)[0];
    if (firstErrorKey && errors[firstErrorKey as keyof typeof errors]?.message) {
      toast({
        title: t('entity_form.common.validation_failed_title'),
        description: errors[firstErrorKey as keyof typeof errors]?.message as string,
        variant: 'destructive',
      });
    } else {
      toast({
        title: t('entity_form.common.validation_failed_title'),
        description: t('entity_form.common.validation_failed_generic_desc'),
        variant: 'destructive',
      });
    }
  };

  if (isLoading || isLoadingLayer) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit, onError)}>
        <Page
          title={t('entity_form.events.edit_dynamic_title', { name: methods.watch('name') || t('entity_form.events.breadcrumb_event') })}
          description={t('entity_form.events.edit_desc')}
          breadcrumbs={[
            { label: t('entity_list.events.title'), href: '/events' },
            { label: methods.watch('name') || t('entity_form.events.breadcrumb_event'), href: `/events/view/${eventId}` },
            { label: t('entity_form.common.edit') }
          ]}
          layoutMode="scrolling"
          actions={
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                color="error"
                disabled={isSaving || isDeleting}
                onClick={() => setShowDeleteConfirm(true)}
                startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : <Delete sx={{ fontSize: '1rem' }} />}
                sx={{ borderRadius: 2 }}
              >
                {t('entity_form.events.delete')}
              </Button>

              <Button
                type="submit"
                variant="contained"
                disabled={isSaving || isDeleting}
                startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Save sx={{ fontSize: '1rem' }} />}
                sx={{ borderRadius: 2, px: 3 }}
              >
                {t('entity_form.events.update')}
              </Button>
            </Stack>
          }
        >

          <DeleteConfirmationModal
            isOpen={showDeleteConfirm}
            onClose={() => setShowDeleteConfirm(false)}
            onConfirm={handleDelete}
            isDeleting={isDeleting}
            itemName={eventData?.name}
            description={t('entity_form.events.delete_desc')}
          />

          <Stack spacing={4}>
            <BasicIdentitySection />
            <RelationshipsSection />
            {novelId && <CustomFieldsSection novelId={novelId} sections={eventSettings} />}
            {novelId && eventData && (
              <EventImplicationsSection
                eventId={eventId}
                eventData={{
                  ...eventData,
                  name: methods.watch('name') ?? eventData.name,
                  order: methods.watch('order') ?? eventData.order,
                  characterIds: methods.watch('characterIds') ?? eventData.characterIds,
                  organizationIds: methods.watch('organizationIds') ?? eventData.organizationIds,
                  locationIds: methods.watch('locationIds') ?? eventData.locationIds,
                  eventChanges: effectiveEventChanges,
                }}
                onSaveOverride={async (entityId, entityType, entityName, changes, reciprocals, description) => {
                  const existingPrimary = effectiveEventChanges.find(c => c.entityId === entityId);
                  const oldReciprocals = existingPrimary ? generateReciprocalDeltas(entityType, entityId, existingPrimary.changes) : [];

                  const allReciprocalEntities = new Map<string, { entityType: PrimaryEntityType; entityName: string; changes: FieldChange[] }>();

                  for (const r of oldReciprocals) {
                    allReciprocalEntities.set(r.entityId, { entityType: r.entityType, entityName: r.entityName || r.entityId, changes: [] });
                  }
                  for (const r of reciprocals) {
                    allReciprocalEntities.set(r.entityId, { entityType: r.entityType, entityName: r.entityName || r.entityId, changes: r.changes });
                  }

                  for (const [recEntityId, rec] of allReciprocalEntities.entries()) {
                    if (rec.changes.length > 0) {
                      if (rec.entityType === 'characters') {
                        const chars = methods.getValues('characterIds') || [];
                        if (!chars.includes(recEntityId)) {
                          methods.setValue('characterIds', [...chars, recEntityId], { shouldDirty: true });
                        }
                      } else if (rec.entityType === 'organizations') {
                        const orgs = methods.getValues('organizationIds') || [];
                        if (!orgs.includes(recEntityId)) {
                          methods.setValue('organizationIds', [...orgs, recEntityId], { shouldDirty: true });
                        }
                      } else if (rec.entityType === 'locations') {
                        const locs = methods.getValues('locationIds') || [];
                        if (!locs.includes(recEntityId)) {
                          methods.setValue('locationIds', [...locs, recEntityId], { shouldDirty: true });
                        }
                      }
                    }
                  }

                  setPendingChanges(prev => {
                    const next = new Map(prev);

                    if (changes.length === 0) {
                      next.set(entityId, {
                        entityId, entityType, entityName, changes, description, isDeleted: true
                      });
                    } else {
                      next.set(entityId, {
                        entityId, entityType, entityName, changes, description, isDeleted: false
                      });
                    }

                    for (const [recEntityId, rec] of allReciprocalEntities.entries()) {
                      if (rec.changes.length === 0) {
                        next.set(recEntityId, {
                          entityId: recEntityId,
                          entityType: rec.entityType,
                          entityName: rec.entityName,
                          changes: rec.changes,
                          description: t('event_implications.reciprocal_change'),
                          isDeleted: true
                        });
                      } else {
                        next.set(recEntityId, {
                          entityId: recEntityId,
                          entityType: rec.entityType,
                          entityName: rec.entityName,
                          changes: rec.changes,
                          description: t('event_implications.reciprocal_change'),
                          isDeleted: false
                        });
                      }
                    }
                    return next;
                  });
                }}
              />
            )}
          </Stack>
        </Page>
        <FloatingSaveButton
          isSaving={isSaving}
          disabled={isDeleting}
          label={t('entity_form.events.update')}
        />
      </form>
    </FormProvider>
  );
}

export default function EditEventPage() {
  const { t } = useTranslation();
  return (
    <AuthGuard>
      <Suspense fallback={<div>{t('entity_form.events.loading')}</div>}>
        <EditEventForm />
      </Suspense>
    </AuthGuard>
  )
}
